import { useState } from 'react'
import { supabase } from '../api/supabase'
import type { InvoiceToMismatch } from '../components/InvoiceToVerifyDialog'
import type { BoxtopReadback } from '../lib/boxtopReadback'
import type { PortProblem } from '../components/PortCodeProblemDialog'
import type { MissingAccountParty } from '../components/MissingBoxtopAccountDialog'

// Z24: candidate BoxTop Contact when a customer's name is 85%+ similar to one already in
// BoxTop's address book -- surfaced so staff can reuse it instead of creating a duplicate.
export interface BoxtopMatchCandidate {
  accountNumber: string
  name: string
  city?: string | null
  country?: string | null
  score: number
  // Z58.9.6: whether this candidate is a standalone top-level Contact, or an existing
  // sub-address row under the job's InvoiceTo parent -- both write the same way, this is
  // purely so staff can see which kind they're picking.
  source?: 'standalone' | 'parent_sub_address'
}

export interface BoxtopAmbiguousParty {
  role: string
  customerId: string
  customerName: string
  customerAddress?: string
  candidates: BoxtopMatchCandidate[]
}

interface SyncResult {
  success: boolean
  boxtraxRef?: string
  // KI26034 P0: BoxTop created the job (200 response) but no recognized
  // reference field was found in it -- true sync failure would be
  // success:false, this is a softer "created but unconfirmed ref" outcome.
  partial?: boolean
  // KI26034 P0: a job already has a BoxTop JobId from a prior partial sync --
  // this call did NOT touch BoxTop at all (no update endpoint exists yet).
  alreadyExists?: boolean
  boxtraxJobId?: string
  error?: string
  // Z24: sync was blocked pending staff resolving an ambiguous BoxTop Contact match for one
  // or more parties -- show BoxtopContactMatchDialog with ambiguousContacts, then retry.
  // Z58.5: 'invoice_to_not_bound'/'invoice_to_address_mismatch' block earlier, before any party
  // matching runs at all -- see verifyInvoiceToAddress in sync-to-boxtrax.
  result?: 'ambiguous_contact_match' | 'invoice_to_not_bound' | 'invoice_to_address_mismatch' | 'port_code_missing' | 'address_not_chosen' | 'address_not_linked' | 'missing_boxtop_account'
  portProblems?: PortProblem[]
  missingParties?: MissingAccountParty[]
  addressNotChosen?: Array<{ role: string; customerName: string; source: string; reason: string; addressText: string }>
  ambiguousContacts?: BoxtopAmbiguousParty[]
  invoiceToMismatch?: InvoiceToMismatch
  // Read-back check: what BoxTop actually stored for this job vs what we sent (read-only GET after CreateJob).
  readback?: BoxtopReadback
}

interface MatchContactResult {
  success: boolean
  result?: 'auto_matched' | 'ambiguous_contact_match' | 'no_match'
  accountNumber?: string
  candidates?: BoxtopMatchCandidate[]
  error?: string
}

export function useSyncToBoxtrax() {
  const [syncingJobs, setSyncingJobs] = useState<Set<string>>(new Set())

  const sync = async (jobRef: string, options: { force?: boolean; invoiceToConfirmed?: boolean } = {}): Promise<SyncResult> => {
    setSyncingJobs(prev => new Set([...prev, jobRef]))
    try {
      const { data: sessionData } = await supabase.auth.getSession()
      const accessToken = sessionData?.session?.access_token || ''

      // Z58.6: tracing every sync attempt's request/response so a "skipped Stage 1" or "stuck
      // on Stage 2" report can be matched against exactly what the backend was asked and returned.
      console.log('[useSyncToBoxtrax] sync request:', { jobRef, force: options.force, invoiceToConfirmed: options.invoiceToConfirmed })

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/sync-to-boxtrax`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken}`
          },
          body: JSON.stringify({ job_ref: jobRef, force: options.force, invoiceToConfirmed: options.invoiceToConfirmed })
        }
      )

      const result = await response.json()
      console.log('[useSyncToBoxtrax] sync response:', { jobRef, result: result.result, success: result.success, hasInvoiceToMismatch: !!result.invoiceToMismatch, hasAmbiguousContacts: !!result.ambiguousContacts?.length })
      return {
        success: result.success === true && !result.error,
        boxtraxRef: result.boxtraxRef,
        partial: result.partial === true,
        alreadyExists: result.alreadyExists === true,
        boxtraxJobId: result.boxtraxJobId,
        error: result.error,
        result: result.result,
        ambiguousContacts: result.ambiguousContacts,
        invoiceToMismatch: result.invoiceToMismatch,
        readback: result.readback,
        portProblems: result.portProblems,
        missingParties: result.missingParties
      }
    } catch (err) {
      return {
        success: false,
        error: err instanceof Error ? err.message : 'Sync failed'
      }
    } finally {
      setSyncingJobs(prev => {
        const next = new Set(prev)
        next.delete(jobRef)
        return next
      })
    }
  }

  const isSyncing = (jobRef: string) => syncingJobs.has(jobRef)

  // Z24: manual "Search BoxTop" button on the Customer detail page -- name-matches against
  // BoxTop's Contact list so staff can pre-associate a customer before it's ever synced on a job.
  const matchContact = async (customerId: string): Promise<MatchContactResult> => {
    try {
      const { data: sessionData } = await supabase.auth.getSession()
      const accessToken = sessionData?.session?.access_token || ''

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/sync-to-boxtrax`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken}`
          },
          body: JSON.stringify({ action: 'match_contact', customer_id: customerId })
        }
      )

      const result = await response.json()
      return {
        success: result.success === true,
        result: result.result,
        accountNumber: result.accountNumber,
        candidates: result.candidates,
        error: result.error
      }
    } catch (err) {
      return {
        success: false,
        error: err instanceof Error ? err.message : 'Match failed'
      }
    }
  }

  // Read-back check on demand for an already-synced job (read-only on BoxTop's side).
  const readbackJob = async (jobRef: string): Promise<{ success: boolean; readback?: BoxtopReadback; error?: string }> => {
    try {
      const { data: sessionData } = await supabase.auth.getSession()
      const accessToken = sessionData?.session?.access_token || ''
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/sync-to-boxtrax`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${accessToken}` },
          body: JSON.stringify({ action: 'readback_job', job_ref: jobRef })
        }
      )
      const result = await response.json()
      return { success: result.success === true, readback: result.readback, error: result.error }
    } catch (err) {
      return { success: false, error: err instanceof Error ? err.message : 'Read-back failed' }
    }
  }

  return { sync, isSyncing, matchContact, readbackJob }
}
